import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

export type GhanttScrollVirtualSlotRange = { start: number; end: number };

export interface GhanttScrollVirtualContextValue {
  /** `true` cuando el host calcula la ventana (hora/día/semana con muchos slots). */
  managed: boolean;
  slotRange: GhanttScrollVirtualSlotRange | null;
}

/** Referencia estable cuando no hay virtualización por scroll. */
// eslint-disable-next-line react-refresh/only-export-components -- constante de snapshot del store
export const GHANTT_SCROLL_VIRTUAL_CTX_IDLE: GhanttScrollVirtualContextValue = Object.freeze({
  managed: false,
  slotRange: null,
});

function rangeEqual(
  a: GhanttScrollVirtualSlotRange | null,
  b: GhanttScrollVirtualSlotRange | null
): boolean {
  if ((a === null || a === undefined) && (b === null || b === undefined)) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return a.start === b.start && a.end === b.end;
}

function snapshotEqual(
  prev: GhanttScrollVirtualContextValue,
  next: GhanttScrollVirtualContextValue
): boolean {
  if (prev === next) return true;
  if (prev.managed !== next.managed) return false;
  if (!prev.managed) return true;
  return rangeEqual(prev.slotRange, next.slotRange);
}

/**
 * Store externo (`useSyncExternalStore`): actualizar la ventana no re-renderiza el organismo Ghantt
 * ni la tabla completa, solo a los suscriptores (p. ej. cabecera con `GhanttSlotStrip`).
 */
export type GhanttScrollVirtualStore = {
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => GhanttScrollVirtualContextValue;
  setIdle: () => void;
  /** `managed === true`; `range === null` hasta la primera medición del viewport. */
  setManagedRange: (range: GhanttScrollVirtualSlotRange | null) => void;
};

// eslint-disable-next-line react-refresh/only-export-components -- factory del store (no es componente React)
export function createGhanttScrollVirtualStore(): GhanttScrollVirtualStore {
  let snapshot: GhanttScrollVirtualContextValue = GHANTT_SCROLL_VIRTUAL_CTX_IDLE;
  const listeners = new Set<() => void>();

  const emit = () => {
    for (const l of listeners) l();
  };

  return {
    subscribe(onStoreChange) {
      listeners.add(onStoreChange);
      return () => {
        listeners.delete(onStoreChange);
      };
    },
    getSnapshot() {
      return snapshot;
    },
    setIdle() {
      if (snapshot === GHANTT_SCROLL_VIRTUAL_CTX_IDLE) return;
      snapshot = GHANTT_SCROLL_VIRTUAL_CTX_IDLE;
      emit();
    },
    setManagedRange(range) {
      const next: GhanttScrollVirtualContextValue =
        range === null
          ? { managed: true, slotRange: null }
          : { managed: true, slotRange: { start: range.start, end: range.end } };
      if (snapshotEqual(snapshot, next)) return;
      snapshot = next;
      emit();
    },
  };
}

const GhanttScrollVirtualStoreContext = createContext<GhanttScrollVirtualStore | null>(null);

export function GhanttScrollVirtualRoot({
  store,
  children,
}: {
  store: GhanttScrollVirtualStore;
  children: ReactNode;
}) {
  return (
    <GhanttScrollVirtualStoreContext.Provider value={store}>
      {children}
    </GhanttScrollVirtualStoreContext.Provider>
  );
}

/**
 * Ventana virtual de índices de slots (cabecera). Implementado con `useSyncExternalStore` para no
 * re-renderizar celdas de tabla al hacer scroll horizontal.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook del store de scroll virtual
export function useGhanttScrollVirtual(): GhanttScrollVirtualContextValue {
  const store = useContext(GhanttScrollVirtualStoreContext);
  const subscribe = useCallback(
    (onStoreChange: () => void) => (store ? store.subscribe(onStoreChange) : () => {}),
    [store]
  );
  const getSnapshot = useCallback(
    () => (store ? store.getSnapshot() : GHANTT_SCROLL_VIRTUAL_CTX_IDLE),
    [store]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
